import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import {
  TrendingUp, BarChart2, Calendar, Loader2, AlertTriangle, Zap, ArrowRight,
  Image, BookOpen, Brain, FileText, Globe, Sparkles, ArrowUpRight,
} from "lucide-react";

const ACTION_META = {
  image_gen_dish: { label: "Imagen plato", color: "#f97316", icon: Image },
  image_gen_ingredient: { label: "Img ingrediente", color: "#fb923c", icon: Image },
  image_gen_logo: { label: "Logo", color: "#a855f7", icon: Sparkles },
  recipe_gen: { label: "Receta", color: "#22c55e", icon: BookOpen },
  menu_gen: { label: "Menu completo", color: "#3b82f6", icon: FileText },
  forecast: { label: "Prevision IA", color: "#06b6d4", icon: BarChart2 },
  invoice_extract: { label: "Extraer factura", color: "#eab308", icon: FileText },
  menu_import: { label: "Importar menu", color: "#ec4899", icon: FileText },
  brand_ambiance: { label: "Identidad visual", color: "#8b5cf6", icon: Sparkles },
  website_content: { label: "Contenido web", color: "#14b8a6", icon: Globe },
  waste_estimate: { label: "Estimar merma", color: "#64748b", icon: Brain },
  allergen_ai: { label: "Alergenos IA", color: "#ef4444", icon: Brain },
  admin_top_up: { label: "Recarga", color: "#10b981", icon: ArrowUpRight },
};

const PERIODS = [
  { value: 7, label: "7 dias" },
  { value: 14, label: "14 dias" },
  { value: 30, label: "30 dias" },
  { value: 90, label: "90 dias" },
];

export function CreditUsageCharts({ restaurantId, subscription = null, topUpPacks = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const d = await api.get(`/subscription/credits/${restaurantId}/usage-stats?days=${period}`);
      setData(d);
    } catch { /* silently fail */ }
    setLoading(false);
  }, [restaurantId, period]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
      </div>
    );
  }

  const hasUsage = data && data.total_credits > 0;

  return (
    <div className="space-y-5">
      {/* ── Projection + smart recharge (only when subscription is provided) ── */}
      {subscription && (
        <ProjectionHeader
          subscription={subscription}
          stats={data}
          topUpPacks={topUpPacks}
          period={period}
        />
      )}

      {!hasUsage ? (
        <div className="text-center py-8 text-muted-foreground text-sm" data-testid="usage-empty-state">
          Sin consumo en este periodo
        </div>
      ) : (
        <>
          {/* Period selector */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground font-medium">Total:</span>
              <span className="font-bold text-sm">{data.total_credits} creditos</span>
              <span className="text-muted-foreground">en {data.total_calls} operaciones</span>
            </div>
            <div className="flex gap-1" data-testid="period-selector">
              {PERIODS.map(p => (
                <button
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
                    period === p.value
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted/60 text-muted-foreground hover:bg-muted"
                  }`}
                  data-testid={`period-${p.value}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* By Action — horizontal bar chart */}
          <ByActionChart actions={data.by_action} total={data.total_credits} />

          {/* By Day — area chart */}
          <ByDayChart days={data.by_day} period={period} />
        </>
      )}
    </div>
  );
}

/* ── Compact projection + smart recharge recommendation ────────────────── */
function ProjectionHeader({ subscription, stats, topUpPacks, period }) {
  const navigate = useNavigate();

  const projection = useMemo(() => {
    const totalConsumed = stats?.total_credits || 0;
    const days = stats?.period_days || period || 7;
    const avgPerDay = totalConsumed / Math.max(1, days);
    const remaining = subscription?.credits_remaining || 0;
    if (avgPerDay <= 0) return { daysLeft: null, avgPerDay: 0 };
    return {
      daysLeft: Math.floor(remaining / avgPerDay),
      avgPerDay: Math.round(avgPerDay * 10) / 10,
    };
  }, [stats, subscription, period]);

  const suggestedPack = useMemo(() => {
    if (!subscription || !topUpPacks?.length || projection.daysLeft === null) return null;
    const daysRemaining = subscription.days_remaining ?? 0;
    if (projection.daysLeft >= daysRemaining) return null;
    const gapDays = daysRemaining - projection.daysLeft;
    const needed = Math.ceil(projection.avgPerDay * gapDays);
    if (needed <= 0) return null;
    const sorted = [...topUpPacks].sort((a, b) => (a.credits || 0) - (b.credits || 0));
    const covering = sorted.find((p) => (p.credits || 0) >= needed);
    return { pack: covering || sorted[sorted.length - 1], needed, gapDays };
  }, [subscription, topUpPacks, projection]);

  const hasProjection = projection.daysLeft !== null && projection.avgPerDay > 0;
  const shortage = hasProjection && projection.daysLeft < (subscription.days_remaining ?? 999);

  if (!hasProjection && !suggestedPack) {
    return (
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground bg-muted/40 rounded-lg px-3 py-2" data-testid="projection-empty">
        <TrendingUp className="w-3.5 h-3.5" />
        <span>Aún no hay consumo suficiente para estimar una proyección.</span>
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border p-4 ${shortage ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-muted/30"}`}
      data-testid="credit-projection-header"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Projection number */}
        <div className="flex-shrink-0">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            <TrendingUp className="w-3 h-3" />
            Proyección
          </div>
          <div className="flex items-baseline gap-1.5 mt-1">
            <span className="text-3xl font-bold tracking-tight" data-testid="projection-days-left">
              {projection.daysLeft > 999 ? "∞" : projection.daysLeft}
            </span>
            <span className="text-xs text-muted-foreground">días restantes</span>
          </div>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            ritmo últimos {stats?.period_days || period} días (~{projection.avgPerDay}/día)
          </p>
        </div>

        {/* Divider on desktop */}
        <div className="hidden sm:block w-px self-stretch bg-border" />

        {/* Shortage alert + smart CTA */}
        <div className="flex-1 min-w-0">
          {shortage ? (
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-700 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                  Se agotarán antes de renovar
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Faltan <strong>{(subscription.days_remaining ?? 0) - projection.daysLeft}</strong> días de cobertura para llegar al próximo ciclo ({subscription.days_remaining} días).
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-emerald-700 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                  Saldo holgado
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Te alcanzan los créditos para cubrir los {subscription.days_remaining} días que restan hasta renovación.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Smart CTA */}
        {suggestedPack && (
          <div className="flex-shrink-0 flex flex-col gap-1 sm:items-end w-full sm:w-auto">
            <button
              type="button"
              onClick={() => navigate(`/app/subscription?tab=top-up&suggested=${suggestedPack.pack.pack_id}`, { replace: false })}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 transition w-full sm:w-auto justify-center"
              data-testid="projection-topup-btn"
            >
              <Zap className="w-3.5 h-3.5" />
              Recargar {suggestedPack.pack.credits} créd. · {suggestedPack.pack.price}€
              <ArrowRight className="w-3 h-3" />
            </button>
            <p className="text-[10px] text-muted-foreground sm:text-right">
              Cubre los {suggestedPack.gapDays} días que faltan
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function ByActionChart({ actions, total }) {
  if (!actions || actions.length === 0) return null;
  const max = actions[0]?.credits || 1;

  return (
    <div data-testid="usage-by-action">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
        <BarChart2 className="w-3.5 h-3.5" />
        Por tipo de accion
      </h4>
      <div className="space-y-2">
        {actions.map(a => {
          const meta = ACTION_META[a.action] || { label: a.action, color: "#94a3b8", icon: Brain };
          const pct = Math.max(4, (a.credits / max) * 100);
          const Icon = meta.icon;
          return (
            <div key={a.action} className="group" data-testid={`action-bar-${a.action}`}>
              <div className="flex items-center gap-2 mb-0.5">
                <Icon className="w-3 h-3 flex-shrink-0" style={{ color: meta.color }} />
                <span className="text-[11px] font-medium flex-1 truncate">{meta.label}</span>
                <span className="text-[11px] text-muted-foreground">{a.calls} ops</span>
                <span className="text-[11px] font-bold min-w-[40px] text-right">{a.credits}</span>
              </div>
              <div className="h-2 bg-muted/50 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${pct}%`, backgroundColor: meta.color }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ByDayChart({ days, period }) {
  const chartData = useMemo(() => {
    if (!days || days.length === 0) return null;
    // Subsample if too many days
    let displayDays = days;
    if (period > 30) {
      // Group by week
      const weeks = [];
      for (let i = 0; i < days.length; i += 7) {
        const chunk = days.slice(i, i + 7);
        const credits = chunk.reduce((s, d) => s + d.credits, 0);
        const calls = chunk.reduce((s, d) => s + d.calls, 0);
        weeks.push({ date: chunk[0].date, credits, calls });
      }
      displayDays = weeks;
    }
    const maxCredits = Math.max(1, ...displayDays.map(d => d.credits));
    return { days: displayDays, maxCredits };
  }, [days, period]);

  if (!chartData || chartData.days.length === 0) return null;

  const { days: displayDays, maxCredits } = chartData;
  const W = 100;
  const H = 40;
  const padX = 0;
  const padY = 2;
  const usableW = W - padX * 2;
  const usableH = H - padY * 2;

  const points = displayDays.map((d, i) => {
    const x = padX + (i / Math.max(1, displayDays.length - 1)) * usableW;
    const y = padY + usableH - (d.credits / maxCredits) * usableH;
    return { x, y, ...d };
  });

  // Create smooth SVG path
  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(" ");
  const areaPath = `${linePath} L ${points[points.length - 1].x.toFixed(2)} ${H} L ${points[0].x.toFixed(2)} ${H} Z`;

  // Labels: show first, mid, last
  const labelIndices = [0, Math.floor(displayDays.length / 2), displayDays.length - 1];

  return (
    <div data-testid="usage-by-day">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
        <Calendar className="w-3.5 h-3.5" />
        Consumo {period > 30 ? "semanal" : "diario"}
      </h4>
      <div className="relative">
        {/* Y axis labels */}
        <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-[9px] text-muted-foreground pr-1" style={{ width: "30px" }}>
          <span>{maxCredits}</span>
          <span>{Math.round(maxCredits / 2)}</span>
          <span>0</span>
        </div>
        <div className="ml-8">
          <svg viewBox={`0 0 ${W} ${H + 8}`} className="w-full" preserveAspectRatio="none" style={{ height: "140px" }}>
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map(f => (
              <line
                key={f}
                x1={padX} y1={padY + usableH * (1 - f)}
                x2={W} y2={padY + usableH * (1 - f)}
                stroke="currentColor" className="text-border" strokeWidth="0.15" strokeDasharray="1 1"
              />
            ))}
            {/* Area */}
            <path d={areaPath} fill="url(#areaGrad)" opacity="0.3" />
            {/* Line */}
            <path d={linePath} fill="none" stroke="#f97316" strokeWidth="0.5" strokeLinecap="round" strokeLinejoin="round" />
            {/* Dots for non-zero days */}
            {points.filter(p => p.credits > 0).map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r="0.6" fill="#f97316" />
            ))}
            {/* Date labels */}
            {labelIndices.map(idx => {
              const p = points[idx];
              if (!p) return null;
              const d = new Date(p.date);
              const label = d.toLocaleDateString("es-ES", { day: "2-digit", month: "short" });
              return (
                <text key={idx} x={p.x} y={H + 5} textAnchor="middle" fill="currentColor" className="text-muted-foreground" fontSize="2.5">
                  {label}
                </text>
              );
            })}
            <defs>
              <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f97316" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>
      {/* Summary below chart */}
      <div className="flex justify-between mt-2 text-[10px] text-muted-foreground ml-8">
        <span>Dia con mas consumo: {(() => {
          const maxDay = displayDays.reduce((a, b) => b.credits > a.credits ? b : a, { credits: 0, date: "" });
          if (!maxDay.date) return "—";
          const d = new Date(maxDay.date);
          return `${d.toLocaleDateString("es-ES", { day: "2-digit", month: "short" })} (${maxDay.credits} cr)`;
        })()}</span>
        <span>Media: {Math.round(displayDays.reduce((s, d) => s + d.credits, 0) / Math.max(1, displayDays.filter(d => d.credits > 0).length))} cr/{period > 30 ? "semana" : "dia"}</span>
      </div>
    </div>
  );
}
